import { FactorType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { NEW_POST_LOGIN_ROUTES } from '../routes';
import { PASSKEY_ENROLL_OFFER_ID } from './postLoginOfferIds';

export const WIX_EMPLOYEES_EMAIL_DOMAIN = 'wix.com';
export const ACCOUNT_SETTINGS_URL =
  'https://manage.wix.com/account/account-settings';

export const LOGIN_PAGE_CONTEXT = 'login';
export const SIGNUP_PAGE_CONTEXT = 'signup';
export const GET_USER_ACCOUNTS_CONTEXT = 'GET_USER_ACCOUNTS_CONTEXT';
export const FEDOPS_APP_NAME = 'login';
export const USERS_BASE_URL = 'https://users.wix.com';
export const LOGIN_MAIN_PAGE_BASIC_URL = `${USERS_BASE_URL}/signin`;
export const ACCOUNT_RECOVERY_URL = 'https://users.wix.com/account-recovery';
export const ACCOUNT_RECOVERY_FORGOT_PASSWORD_URL =
  'https://users.wix.com/account-recovery/forgot-password';

export const NEW_ACCOUNT_RECOVERY_URL =
  'https://users.wix.com/new-account-recovery';

export const OPEN_WIX_APP_URL =
  'https://manage.wix.app/?s=true&d=https://www.wix.com';

export const WIX_HOME_URL = 'https://www.wix.com/';
export const WIXEL_HOME_URL = 'https://www.wix.com/wixel';
export const STUDIO_HOME_URL = 'https://www.wix.com/studio';
export const API_PATH = '/login/api/';
export const USERS_API_PATH = '/_api/wix-html-login-webapp';

/**
 * RFC 8628 OAuth 2.0 Device Authorization Grant — device (client) side.
 * The fixed OAuth client the QR-login device screen authorizes against.
 */
export const DEVICE_AUTH_CLIENT_ID = 'fa6ddbe4-004a-4f18-b8cd-3fb8ccf6811d';
/** scope MUST include `openid` so an id_token is minted (per the device-code contract). */
export const DEVICE_AUTH_SCOPE = 'openid';
/** Public OAuth token endpoint polled with the device_code grant. */
export const OAUTH_TOKEN_PATH = '/oauth/token';
/**
 * RFC 8628 §3.1 device authorization endpoint — requests the device + user code.
 * Served same-origin on users.wix.com, so it is called directly. (The oauth
 * ambassador client's `deviceCode` maps to `/v1/oauth/device/code`, which is not
 * routed on the users.wix.com domain and 404s.)
 */
export const DEVICE_AUTHORIZE_PATH = '/oauth/device/authorize';
/** RFC 8628 grant type used when polling the token endpoint. */
export const DEVICE_AUTH_GRANT_TYPE =
  'urn:ietf:params:oauth:grant-type:device_code';
/**
 * IDP connection used to exchange the device-flow OIDC id_token for a real Wix
 * session via IAM `loginWithIdpConnectionTokenParams` (POST /v1/sso/login-with-token-params).
 */
export const DEVICE_AUTH_IDP_CONNECTION_ID =
  'e85eef25-41eb-486a-ad8a-aa0c48481df4';
/**
 * The `tokenParams` key the IDP connection SPI reads the OIDC id_token from.
 * IAM defines `ID_TOKEN = "idToken"` (camelCase) in AuthorizeRequestDomain; the
 * Google/Apple/OIDC SPIs throw `ParamNotProvidedException("idToken")` when it's
 * absent, so this must match exactly.
 */
export const DEVICE_AUTH_ID_TOKEN_PARAM = 'idToken';
/** Default poll interval (seconds) when the server omits `interval` (RFC 8628 §3.5). */
export const DEVICE_AUTH_DEFAULT_INTERVAL = 5;
/** OAuth error codes returned by the token endpoint while polling. */
export const DEVICE_AUTH_TOKEN_ERRORS = {
  AUTHORIZATION_PENDING: 'authorization_pending',
  EXPIRED_TOKEN: 'expired_token',
  /** Missing required parameter (client_id / device_code) — terminal, fix the request. */
  INVALID_REQUEST: 'invalid_request',
  /** Transient server-side failure while minting the token — retry, restart if it persists. */
  UNKNOWN_ERROR: 'unknown_error',
  ACCESS_DENIED: 'access_denied',
} as const;
export const IS_BLANK_GRAVATAR =
  /(https?:\/\/(.+?\.)?gravatar\.com(\/[A-Za-z0-9\-\._~:\/\?#\[\]@!$&'\(\)\*\+,;\=]*)?)(\?|&)((d=blank)(&?))+/g;
export const AUTH_METHODS_BY_MAIL = {
  LOGIN_PASSWORD: 'LOGIN_PASSWORD',
  SIGNUP_PASSWORD: 'SIGNUP_PASSWORD',
  SINGLE: 'SINGLE',
  MULTI: 'MULTI',
  SOCIAL_ONLY: 'SOCIAL_ONLY',
  WIX_EMPLOYEE: 'WIX_EMPLOYEE',
} as const;
export const RESEND_INDICATION_TTL_TIMER = 30000;
export const CAPTCHA_KEY = 'recaptchaParams.recaptcha_response_field';

export const PARTNERS_API_PATH = '_api/partners-dashboard-server';
export const ACCOUNT_SETTINGS_API_PATH = '_api/account-settings-server';
export const ACCOUNT_SERVER_API_PATH = '_api/account-server/v1/';
export const FETCH_OFFERS_URL =
  '/_api/dealer-offers-serving-service/v1/dealer/serving/offers/list';
export const OWNER_AUTHENTICATOR_PATH = '_api/verification-web/v1/challenges';
export const IAM = 'iam';
export const EMAIL_CONFIRM_ENFORCEMENT = 'emailConfirmationEnforcement';
export const SESSION_STORAGE_KEYS = {
  PENDING_EMAIL_CONFIRMATION: 'wix-signup-email-verification-pending',
  REGISTRATION_PAYLOAD: 'registration_payload',
  VERIFICATION_ID: 'verification_id',
};

export const FORGOT_PASSWORD_LINK = (
  sessionId: string,
  overrideLocale?: string,
) => {
  const withLocale = overrideLocale ? `&overrideLocale=${overrideLocale}` : '';
  return `/account-recovery/forgot-password?sessionId=${sessionId}${withLocale}`;
};

export const ConfirmaitonCodeActions = {
  SEND: 'send',
  RESEND: 'resend',
} as const;

export type Values<T> = T[keyof T];

export const POST_LOGIN_OFFERS = {
  CONFIRM_EMAIL: 'confirm-email',
  CONFIRM_DETAILS: 'confirm-details',
  RECOVERY_PHONE: 'recovery-phone',
  UPDATE_PASSWORD: 'update-password',
  CREATE_PASSWORD: 'create-password',
  MFA: 'mfa-phone',
  MFA_AGGRESSIVE: 'aggressive-2fa',
  OWNER_APP_2FA: 'owner-app-2fa',
  PREMIUM_USERS_2FA_ENFORCEMENT: 'premium-users-2fa-enforcement',
  WIX_APP_ENFORCEMENT: 'wix-app-enforcement',
  CONFIRM_EXIT_ENFORCEMENT: 'confirm-exit-enforcement',
  PASSKEY_ENROLL: PASSKEY_ENROLL_OFFER_ID,
} as const;

export const TwoFactorAuthMethods = {
  TOTP: 'TOTP',
  Phone: 'Phone',
  Email: 'Email',
  Push: 'Push',
  Unused: 'Unused',
  WebAuthn: 'WebAuthn',
  RecoveryCode: 'RecoveryCode',
} as const;

export const twoFactorSwitchMethodKeysMap: Record<TwoFactorAuthMethod, string> =
  {
    [TwoFactorAuthMethods.TOTP]: 'enter_code.totp.switch_method',
    [TwoFactorAuthMethods.Phone]: 'enter_code.phone.switch_method',
    [TwoFactorAuthMethods.Email]: 'enter_code.email.switch_method',
    [TwoFactorAuthMethods.Push]: 'enter_code.push.switch_method',
    [TwoFactorAuthMethods.WebAuthn]: 'enter_code.webauthn.switch_method',
    [TwoFactorAuthMethods.RecoveryCode]:
      'enter_code.recovery_code.switch_method',
    [TwoFactorAuthMethods.Unused]: 'enter_code.unused.switch_method',
  };

export const twoFactorDescriptionKeysMap: Record<TwoFactorAuthMethod, string> =
  {
    [TwoFactorAuthMethods.TOTP]: 'enter_code.totp.description',
    [TwoFactorAuthMethods.Phone]: 'enter_code.phone.description',
    [TwoFactorAuthMethods.Email]: 'enter_code.email.description',
    [TwoFactorAuthMethods.Push]: 'enter_code.push.description',
    [TwoFactorAuthMethods.WebAuthn]: 'enter_code.webauthn.description',
    [TwoFactorAuthMethods.RecoveryCode]: 'enter_code.recovery_code.description',
    [TwoFactorAuthMethods.Unused]: 'enter_code.unused.description',
  };

export const TwoFactorAuthPhoneDeliveryMethods = {
  SMS: 'SMS',
  VOICE_CALL: 'VOICE_CALL',
} as const;

export const SEARCH_PARAMS = {
  TARGET_URL: 'targetUrl',
  EMAIL: 'email',
  DEFAULT_EMAIL: 'defaultEmail',
  POST_LOGIN_URL: 'postLogin',
  ORIGIN_URL: 'originUrl',
  REDIRECT_TO_URL: 'redirectTo',
  SHOW_WORKSPACE_SELECTOR: 'showWorkspaceSelector',
  POST_SIGNUP_URL: 'postSignUp',
  POST_LOGIN_OFFER: 'postLoginOffer',
  USER_DETAILS: 'userDetails',
  EMAIL_TOKEN: 'emailToken',
  GOOGLE_STATE: 'state',
  GOOGLE_ID_TOKEN: 'id_token',
  COLOR: 'color',
  USER_TOKEN: 'userToken',
  REFERRAL_INFO: 'referralInfo',
  INVITE_TOKEN: 'inviteToken',
  SEND_EMAIL: 'sendEmail',
  LOGIN_DIALOG_CONTEXT: 'loginDialogContext',
  ORIGIN_CONTEXT: 'originContext',
  FORCE_RENDER: 'forceRender',
  IS_STUDIO: 'studio',
  BACK_BUTTON_VISIBILITY: 'backButtonVisibility',
  PARTNER: 'partner',
} as const;

export type TwoFactorAuthMethod =
  | typeof TwoFactorAuthMethods.TOTP
  | typeof TwoFactorAuthMethods.Phone
  | typeof TwoFactorAuthMethods.Email
  | typeof TwoFactorAuthMethods.Push
  | typeof TwoFactorAuthMethods.Unused
  | typeof TwoFactorAuthMethods.WebAuthn
  | typeof TwoFactorAuthMethods.RecoveryCode;

export type TwoFactorAuthPhoneDeliveryMethod =
  | typeof TwoFactorAuthPhoneDeliveryMethods.SMS
  | typeof TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL;
export type IAUTH_METHODS_BY_MAIL = Values<typeof AUTH_METHODS_BY_MAIL>;

export const OFFERS_TO_NEW_ROUTES = {
  [POST_LOGIN_OFFERS.CONFIRM_DETAILS]: NEW_POST_LOGIN_ROUTES.CONFIRM_DETAILS,
  [POST_LOGIN_OFFERS.CONFIRM_EMAIL]: NEW_POST_LOGIN_ROUTES.CONFIRM_EMAIL,
  [POST_LOGIN_OFFERS.MFA]: NEW_POST_LOGIN_ROUTES.PHONE_2FA,
  [POST_LOGIN_OFFERS.MFA_AGGRESSIVE]:
    NEW_POST_LOGIN_ROUTES.PHONE_2FA_AGGRESSIVE,
  [POST_LOGIN_OFFERS.UPDATE_PASSWORD]: NEW_POST_LOGIN_ROUTES.CHANGE_PASSWORD,
  [POST_LOGIN_OFFERS.CREATE_PASSWORD]: NEW_POST_LOGIN_ROUTES.CREATE_PASSWORD,
  [POST_LOGIN_OFFERS.RECOVERY_PHONE]: NEW_POST_LOGIN_ROUTES.RECOVERY_PHONE,
  [POST_LOGIN_OFFERS.OWNER_APP_2FA]: NEW_POST_LOGIN_ROUTES.OWNER_APP_2FA,
  [POST_LOGIN_OFFERS.PREMIUM_USERS_2FA_ENFORCEMENT]:
    NEW_POST_LOGIN_ROUTES.PREMIUM_USERS_2FA_ENFORCEMENT,
  [POST_LOGIN_OFFERS.CONFIRM_EXIT_ENFORCEMENT]:
    NEW_POST_LOGIN_ROUTES.PREMIUM_USERS_CONFIRM_EXIT_ENFORCEMENT,
  [POST_LOGIN_OFFERS.WIX_APP_ENFORCEMENT]:
    NEW_POST_LOGIN_ROUTES.WIX_APP_ENFORCEMENT,
  [POST_LOGIN_OFFERS.PASSKEY_ENROLL]: NEW_POST_LOGIN_ROUTES.PASSKEY_ENROLL,
} as const;

export const DEVICE_LOGIN_METHODS = {
  SIGNUP: 'SIGNUP',
  CONNECTION_SUCCESSFUL: 'CONNECTION_SUCCESSFUL',
  DISCONNECT_SUCCESSFUL: 'DISCONNECT_SUCCESSFUL',
} as const;

export const WIX_PARENT_ACCOUNT_HINT = 'wix';
export const TWOFA_PROCESS = '2fa-process';
export const twoFAProcessMethod = (method: TwoFactorAuthMethod) =>
  '2fa-process-' + method.toLocaleLowerCase();

export type CaptchaStatus = {
  [LOGIN_PAGE_CONTEXT]: boolean;
  [SIGNUP_PAGE_CONTEXT]: boolean;
  [GET_USER_ACCOUNTS_CONTEXT]: boolean;
};

export const GOOGLE_IDP_CONNECTION_ID = '0e6a50f5-b523-4e29-990d-f37fa2ffdd69';
export const FACEBOOK_IDP_CONNECTION_ID =
  '3ecad13f-52c3-483d-911f-31dbcf2a6d23';
export const APPLE_IDP_CONNECTION_ID = 'ce1be235-3691-4532-906c-5a7d2e3acdd1';
export const WIX_TENANT_ID = '11111111-1111-1111-1111-111111111111';

export const EXPERIMENTS = {
  ACCOUNT_RECOVERY_NEW_API: 'specs.ident.accountRecoveryNewApi',
  USE_IAM_FOR_USERS_CALLS: 'specs.ident.useIAMPlatformForUsers',
  USE_IAM_FOR_USERS_LOGIN_CALLS: 'specs.ident.useIAMPlatformForLogin',
  SHOULD_DISPLAY_GOT_ON_SIGNUP_PAGE: 'specs.ident.signupGOT',
  SHOULD_MULTI_POLLING: 'specs.ident.ShouldMultiPolling',
  USE_IAM_FOR_PUSH_FACTOR: 'specs.ident.useIAMPlatformForPushFactor',
  SHOULD_DISPLAY_EMAIL_CONFIRM_POST_SIGNUP:
    'specs.users.ShouldAvoidSendingVerificationEmailAfterUserRegistration',
  SHOULD_SUPPORT_FALLBACK_FACTOR: 'specs.ident.shouldSupportFallbackFactor',
  USE_IAM_PLATFORM_FOR_VOICE_CALL:
    'specs.ident.useIAMPlatformForVoiceCallFactor',
  SHOULD_USE_SERVER_OAUTH_FOR_GOOGLE_LOGIN:
    'specs.users.ShouldUseServerOAuthForGoogleLogin',
  SHOULD_USE_SERVER_OAUTH_FOR_FACEBOOK_LOGIN:
    'specs.users.ShouldUseServerOAuthForFacebookLogin',
  SHOULD_USE_SERVER_OAUTH_FOR_APPLE_LOGIN:
    'specs.users.ShouldUseServerOAuthForAppleLogin',
  // Gates the device-authorization user_code display. Off by default — manual
  // user_code entry isn't supported yet, so for now only the QR is shown.
  DEVICE_AUTH_SHOW_USER_CODE: 'specs.ident.deviceAuthShowUserCode',
};

// The default `users` scope is conducted server-side in src/server.ts
// and passed to the client as window.__EXPERIMENTS__. Add only dynamic
// client-side experiment scopes here.
export const EXPERIMENT_SCOPES = {
  EMAIL_CONFIRMATION_ENFORCEMENT: 'users-email-confirmation-enforcement',
};

export const IFRMAE_EVENTS = {
  LOADED: 'loaded',
  AUTHENTICATION_FAILED: 'authentication_failed',
  AUTHENTICATION_COMPLETED: 'authentication_completed',
};

export const legacyFactorTypeToIamFactorType = (
  twoFAMethod: string,
  phoneDeliveryMethod?: TwoFactorAuthPhoneDeliveryMethod | string,
): FactorType => {
  if (phoneDeliveryMethod === TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL) {
    return FactorType.CALL;
  }

  switch (twoFAMethod) {
    case TwoFactorAuthMethods.TOTP:
      return FactorType.TOTP;
    case TwoFactorAuthMethods.Phone:
      return FactorType.SMS;
    case TwoFactorAuthMethods.Email:
      return FactorType.EMAIL;
    case TwoFactorAuthMethods.Push:
      return FactorType.PUSH;
    case TwoFactorAuthMethods.WebAuthn:
      return FactorType.WEBAUTHN;
    default:
      return FactorType.UNKNOWN_FACTOR_TYPE;
  }
};

export const factorsMapLegacyToIam: {
  [key in TwoFactorAuthMethod]: FactorType;
} = {
  TOTP: FactorType.TOTP,
  Phone: FactorType.SMS,
  Email: FactorType.EMAIL,
  Push: FactorType.PUSH,
  Unused: FactorType.UNKNOWN_FACTOR_TYPE,
  WebAuthn: FactorType.WEBAUTHN,
  RecoveryCode: FactorType.RECOVERY_CODE,
};

export const factorsMapIamToLegacy: {
  [key in FactorType]: TwoFactorAuthMethod;
} = {
  TOTP: TwoFactorAuthMethods.TOTP,
  SMS: TwoFactorAuthMethods.Phone,
  EMAIL: TwoFactorAuthMethods.Email,
  PUSH: TwoFactorAuthMethods.Push,
  UNKNOWN_FACTOR_TYPE: TwoFactorAuthMethods.Unused,
  CALL: TwoFactorAuthMethods.Unused,
  PASSWORD: TwoFactorAuthMethods.Unused,
  WEBAUTHN: TwoFactorAuthMethods.WebAuthn,
  RECOVERY_CODE: TwoFactorAuthMethods.RecoveryCode,
};
